
import { useState, useEffect } from "react";
import { useContentOperations } from "@/hooks/useContentOperations";
import { useAuth } from "@/contexts/AuthContext";
import { useThumbnailExtraction } from "@/hooks/useThumbnailExtraction";
import CollectionHeader from "./CollectionHeader";
import { useCollectionContent } from "@/hooks/useCollectionContent";
import CollectionContent from "./collection-grid/CollectionContent";
import { ViewMode } from "@/hooks/usePersistentViewMode";

interface CollectionGridProps {
  title?: string;
  description?: string;
  collectionId?: string;
  collectionColor?: string;
  isArchive?: boolean;
  onEditContent?: (contentId: string, title: string) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  selectedTagIds?: string[];
  tagLogicType?: "AND" | "OR";
}

const CollectionGrid = ({ 
  title = "All Content", 
  description = "", 
  collectionId, 
  collectionColor,
  isArchive = false,
  onEditContent,
  viewMode: externalViewMode,
  onViewModeChange: externalViewModeChange,
  selectedTagIds = [],
  tagLogicType = "AND"
}: CollectionGridProps) => {
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>("grid");
  const { user } = useAuth();
  const { handleContentClick, handleDelete, handleArchive } = useContentOperations();
  const { processAllPendingThumbnails } = useThumbnailExtraction();
  
  const viewMode = externalViewMode !== undefined ? externalViewMode : internalViewMode;
  
  const handleViewModeChange = (mode: ViewMode) => {
    if (externalViewModeChange) {
      externalViewModeChange(mode);
    } else {
      setInternalViewMode(mode);
    }
  };

  // Log the tag filtering parameters for debugging
  useEffect(() => {
    // Ensure selectedTagIds is always an array
    const tagIds = Array.isArray(selectedTagIds) ? selectedTagIds : [];
    
    if (tagIds.length > 0) {
      console.log(`CollectionGrid: Filtering with ${tagIds.length} tags using ${tagLogicType} logic`);
      console.log("Selected tag IDs:", tagIds);
    }
  }, [selectedTagIds, tagLogicType]);

  const { contentItems, isLoading, refetch, pinContent, unpinContent } = useCollectionContent({
    collectionId,
    isArchive,
    selectedTagIds: Array.isArray(selectedTagIds) ? selectedTagIds : [],
    tagLogicType
  });

  // Keep thumbnail processing in background but remove the refresh button
  useEffect(() => {
    const processPendingThumbnails = async () => {
      try {
        await processAllPendingThumbnails();
      } catch (error) {
        console.error("Error processing pending thumbnails:", error);
      }
    };

    processPendingThumbnails();
    
    const intervalId = setInterval(processPendingThumbnails, 60000);
    
    return () => clearInterval(intervalId);
  }, [processAllPendingThumbnails]);

  console.log("[COLOR DEBUG] CollectionGrid rendering with color:", collectionColor);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Fixed header container that stays above the scrollable content */}
      <div className="flex-none" style={{ zIndex: 10 }}>
        <CollectionHeader
          title={title || ""}
          description={description}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />
      </div>

      {/* Scrollable content area - Remove all padding when in network or freeform view */}
      <div className={`flex-1 ${viewMode === 'freeform' || viewMode === 'network' ? '' : 'px-7'} overflow-auto`}>
        <CollectionContent
          contentItems={contentItems}
          isLoading={isLoading}
          viewMode={viewMode}
          onContentClick={handleContentClick}
          onDeleteContent={handleDelete}
          onArchiveContent={handleArchive}
          onEditContent={onEditContent}
          onPin={pinContent}
          onUnpin={unpinContent}
          collectionColor={collectionColor}
        />
      </div>
    </div>
  );
};

export default CollectionGrid;
